import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { mockData } from '../mock';
import { Sparkles, Clock, FileText } from 'lucide-react';
import axios from 'axios';
import { QuoteModal } from './QuoteModal';
import { NotifyModal } from './NotifyModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImage = (img) => {
  if (!img) return img;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  return img;
};

export const Products = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });
  const [products, setProducts] = useState(mockData.products);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        if (res.data.products && res.data.products.length > 0) {
          setProducts(res.data.products);
        }
      } catch (error) {
        console.error('Error fetching products, using fallback:', error);
      }
    };
    fetchProducts();
  }, []);

  return (
    <section id="products" ref={containerRef} className="relative py-32 px-6 bg-gradient-to-b from-[#0a1628] via-[#0f1f3a] to-[#0a1628]">
      <div className="container mx-auto max-w-7xl">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#7dd3fc]/10 rounded-full mb-6"
          >
            <Sparkles className="text-[#7dd3fc]" size={20} />
            <span className="text-[#7dd3fc] font-semibold">Nossos Produtos</span>
          </motion.div>
          
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Qualidade <span className="text-[#7dd3fc]">Premium</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Seleção exclusiva de gelos saborizados e tradicionais
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 100 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ y: -15 }}
              className="group relative"
              data-testid={`product-card-${product.id}`}
            >
              {/* Card */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm border border-white/10 hover:border-[#7dd3fc]/50 transition-all duration-500">
                {/* Image Container */}
                <div className="relative h-80 overflow-hidden">
                  <motion.img
                    src={resolveImage(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/50 to-transparent" />
                  
                  {/* Badge */}
                  {product.available ? (
                    product.featured && (
                      <motion.div
                        initial={{ x: -100 }}
                        animate={{ x: 0 }}
                        transition={{ delay: 0.5 + index * 0.2 }}
                        className="absolute top-6 left-6 px-4 py-2 bg-[#7dd3fc] rounded-full"
                      >
                        <span className="text-[#0a1628] font-bold text-sm">Destaque</span>
                      </motion.div>
                    )
                  ) : (
                    <motion.div
                      initial={{ x: -100 }}
                      animate={{ x: 0 }}
                      transition={{ delay: 0.5 + index * 0.2 }}
                      className="absolute top-6 left-6 px-4 py-2 bg-[#0a1628]/80 backdrop-blur-sm border border-[#7dd3fc]/50 rounded-full"
                    >
                      <span className="text-[#7dd3fc] font-bold text-sm">Em Breve</span>
                    </motion.div>
                  )}

                  {/* Weight Badge */}
                  <div className="absolute top-6 right-6 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/20">
                    <span className="text-white font-bold text-sm">{product.weight}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#7dd3fc] transition-colors duration-300">
                    {product.name}
                  </h3>
                  
                  <p className="text-white/60 mb-6 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Flavors */}
                  {product.flavors && (
                    <div className="mb-6">
                      <p className="text-[#7dd3fc] text-sm font-semibold mb-3">Sabores disponíveis:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.flavors.map((flavor, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-white/5 rounded-full text-white/70 text-xs border border-white/10"
                          >
                            {flavor}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA Button */}
                  {product.available ? (
                    <motion.button
                      onClick={() => {
                        const text = `Olá, Gostaria de solicitar um orçamento para ${product.name} (${product.weight}).`;
                        window.open(`https://wa.me/5511981919933?text=${encodeURIComponent(text)}`, '_blank');
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full py-4 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold rounded-xl hover:shadow-lg hover:shadow-[#7dd3fc]/30 transition-all duration-300"
                      data-testid={`product-quote-btn-${product.id}`}
                    >
                      Solicitar Orçamento
                    </motion.button>
                  ) : (
                    <button
                      onClick={() => setNotifyProduct(product.name)}
                      className="w-full py-4 bg-[#0a1628] border-2 border-[#7dd3fc]/50 text-[#7dd3fc] font-bold rounded-xl hover:bg-[#7dd3fc]/10 transition-colors flex items-center justify-center gap-2"
                      data-testid={`product-coming-soon-${product.id}`}
                    >
                      <Clock size={18} />
                      <span>EM BREVE</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quote CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-white/60 mb-6 text-lg">Precisa de uma cotação personalizada?</p>
          <motion.button
            onClick={() => setQuoteOpen(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold rounded-xl shadow-xl shadow-[#7dd3fc]/30"
            data-testid="products-quote-cta"
          >
            <FileText size={20} />
            <span>Cotações</span>
          </motion.button>
        </motion.div>
      </div>

      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} products={products} />
      <NotifyModal open={!!notifyProduct} onClose={() => setNotifyProduct(null)} productName={notifyProduct || ''} />
    </section>
  );
};
